import React, {
  useEffect,
  useRef,
  useState,
  useContext,
  useCallback,
} from 'react'

import { Form, Formik, FormikHelpers } from 'formik'
import { LoaderContext } from '../context/FormLoader'
import axios from 'axios'
import { Button } from 'semantic-ui-react'
import { observer } from 'mobx-react-lite'
import AboutCustomer from './feedbacktypes/AboutCustomer'
import FeedbackService from '../../services/FeedbackService'
import {
  FeedbackTypes,
  IFeedbackLookupData,
  FeedbackFormValues,
  IFeedbackValidator,
  FeedbackFieldMapping,
  Feedback,
  IServiceArea,
} from '../../models/Feedback'
import useErrorHandler from '../../hooks/useErrorHandler'
import FormErrorDisplay from '../common/FormErrorMessages'
import Conditional from '../common/Conditional'
import FWOSelectInput from '../form/FWOSelectInput'
import { SelectItem } from '../../models/SelectIem'
import {
  buildLookupSelectItems,
  findById,
} from '../../utilities/commonFunctions'
import ServiceFeedback from './feedbacktypes/ServiceFeedback'
import Complaint from './feedbacktypes/Complaint'
import DecisionReview from './feedbacktypes/DecisionReview'
import CreateFeedbackHeader from './CreateFeedbackHeader'
import CreateFeedbackFooter from './CreateFeedbackFooter'
import './Feedback.css'
import { FeedbackValidator } from './FeedbackValidator'
import FeedbackCreatedConfirmation from './FeedbackCreatedConfirmation'
import { MainArticleLayout } from '../../layouts/MainArticleLayout'

const CreateFeedbackMain = observer(() => {
  const formRef = useRef(null)
  const aboutComponent = useRef(null)
  const serviceFeedbackComponent = useRef(null)
  const complaintComponent = useRef(null)
  const decisionReviewComponent = useRef(null)
  const loader = useContext(LoaderContext)
  const [userErrors, , handleErrors, stringifyErrors, clearErrors] =
    useErrorHandler()
  const mountedRef = useRef(false)
  const [isloading, setIsLoading] = useState(true)
  const [feedbackTypes, setFeedbackTypes] = useState<SelectItem[]>([])
  const [lookupData, setlookupData] = useState<IFeedbackLookupData>()
  const [feedbackSelection, setFeedbackSelection] = useState({
    selection: null,
    clear: false,
    values: new FeedbackFormValues(),
  })
  const [submissionSuccessful, setsubmissionSuccessful] = useState(false)
  const [responseRequired, setResponseRequired] = useState('')

  const [validatorOptions, setValidatorOptions] = useState<IFeedbackValidator>({
    feedbackTypes: [],
    serviceAreaAi: { id: '', name: '', servicePoints: [] },
  })
  const [serviceAreas, setServiceAreas] = useState<IServiceArea[]>([])
  const serviceAreaAi = 'AI queries'

  useEffect(() => {
    mountedRef.current = true
    loader.up()
    const source = axios.CancelToken.source()
    if (mountedRef.current) {
      FeedbackService.getLookupData(source)
        .then((response) => {
          setFeedbackTypes(
            buildLookupSelectItems(response.result?.feedbackTypes, true)
          )
          setlookupData((prevState) => {
            return {
              ...prevState,
              serviceAreas: response.result?.serviceAreas,
              servicePoints: response.result?.servicePoints,
              serviceIssues: response.result?.serviceIssues,
              feedbackTypes: response.result?.feedbackTypes,
              customerReferenceTypes: response.result?.customerReferenceTypes,
              responseRequiredOptions: response.result?.responseRequiredOptions,
            }
          })
          setServiceAreas(response.result?.serviceAreas)
          setValidatorOptions((prevState) => {
            return {
              ...prevState,
              feedbackTypes: response.result?.feedbackTypes,
              serviceAreaAi: response.result?.serviceAreas.find(
                (a) => a.name.toLowerCase() === serviceAreaAi.toLowerCase()
              ),
            }
          })
        })
        .catch((error) => {
          handleErrors(error)
        })
        .finally(() => {
          setIsLoading(false)
          loader.down()
          window.scroll(0, 0)
        })
    }
    return () => {
      mountedRef.current = false
    }
  }, [])

  const renderForm = () => {
    if (feedbackSelection.selection === null) {
      return null
    }
    switch (feedbackSelection.selection.toLowerCase()) {
      case String(FeedbackTypes.ProvideFeedback).toLowerCase():
        return (
          <ServiceFeedback
            ref={serviceFeedbackComponent}
            serviceAreaId="serviceAreaId"
            servicePointId="servicePointId"
            serviceIssueId="serviceIssueId"
            issue="issue"
            libraryPageReference="libraryPageReference"
            lookupData={lookupData}
          />
        )
      case String(FeedbackTypes.MakeComplaint).toLowerCase():
        return (
          <Complaint
            ref={complaintComponent}
            serviceAreaId="serviceAreaId"
            servicePointId="servicePointId"
            serviceIssueId="serviceIssueId"
            issue="issue"
            libraryPageReference="libraryPageReference"
            lookupData={lookupData}
          />
        )
      case String(FeedbackTypes.RequestReviewDecision).toLowerCase():
        return (
          <DecisionReview
            ref={decisionReviewComponent}
            decision="decision"
            reasonBehindWrongDecision="reasonBehindWrongDecision"
            evidenceForWrongDecision="evidenceForWrongDecision"
            expectedOutcome="expectedOutcome"
          />
        )
      default:
        return null
    }
  }

  const onFeedbackSelection = (value: string) => {
    const feedbackType = findById(feedbackTypes, value)
    setFeedbackSelection((prevState) => ({
      ...prevState,
      selection: feedbackType,
      clear: false,
    }))

    if (
      feedbackType.toLowerCase() ===
      String(FeedbackTypes.MakeComplaint).toLowerCase()
    ) {
      setlookupData((prevState) => {
        return {
          ...prevState,
          serviceAreas: serviceAreas.filter(
            (a) => a.name.toLowerCase() !== serviceAreaAi.toLowerCase()
          ),
        }
      })
    } else {
      setlookupData((prevState) => {
        return {
          ...prevState,
          serviceAreas: serviceAreas,
        }
      })
    }

    if (serviceFeedbackComponent.current) {
      serviceFeedbackComponent.current.clear()
    }

    if (complaintComponent.current) {
      complaintComponent.current.clear()
    }
    if (decisionReviewComponent.current) {
      decisionReviewComponent.current.clear()
    }
  }

  const startAgain = () => {
    clearErrors()
    window.location.reload()
    if (serviceFeedbackComponent.current) {
      serviceFeedbackComponent.current.clear()
    }

    if (complaintComponent.current) {
      complaintComponent.current.clear()
    }
    if (decisionReviewComponent.current) {
      decisionReviewComponent.current.clear()
    }
    if (aboutComponent.current) {
      aboutComponent.current.clear()
    }
    setFeedbackSelection((prevState) => ({
      ...prevState,
      selection: '',
      clear: true,
      values: new FeedbackFormValues(),
    }))

    window.scrollTo(0, 0)
  }

  const submit = async () => {
    clearErrors()

    const errors = await formRef.current.validateForm()
    stringifyErrors(errors, FeedbackFieldMapping)
    if (Object.keys(errors).length > 0) {
      window.scrollTo(0, 0)
    }

    formRef.current.submitForm()
  }

  const submitFeedbackForm = async (values: FeedbackFormValues) => {
    const payload = new Feedback(values)
    return await FeedbackService.createFeedback(payload)
  }

  const handleSubmit = useCallback(
    (
      feedbackFormFields: FeedbackFormValues,
      actions: FormikHelpers<FeedbackFormValues>
    ) => {
      setResponseRequired(feedbackFormFields.responseRequired)
      return submitFeedbackForm(feedbackFormFields)
        .then(() => {
          setsubmissionSuccessful(true)
          window.scrollTo(0, 0)
        })
        .catch((error) => {
          handleErrors(error)
          window.scrollTo(0, 0)
        })
        .finally(() => {
          actions.setSubmitting(false)
        })
    },
    []
  )

  return (
    <>
      <MainArticleLayout>
        <article>
          <FormErrorDisplay
            show={userErrors?.length > 0}
            errorsList={userErrors}
            heading="Please review the following errors"
          />
          {!submissionSuccessful ? (
            <>
              <p></p>

              <CreateFeedbackHeader />
              <Conditional if={!isloading}>
                <Formik
                  innerRef={formRef}
                  initialValues={feedbackSelection.values}
                  validateOnMount={false}
                  validationSchema={new FeedbackValidator(
                    validatorOptions
                  ).getRule()}
                  onSubmit={async (values, actions) => {
                    await handleSubmit(values, actions)
                  }}
                >
                  {({ isSubmitting, setTouched, dirty, isValid }) => (
                    <Form className="ui form osp-variant large">
                      <FWOSelectInput
                        className="feedbackTypes"
                        name="feedbackTypeId"
                        label="I want to: "
                        placeholder="[Choose one]"
                        required={true}
                        options={feedbackTypes}
                        loading={false}
                        onSelection={onFeedbackSelection}
                        clearSelection={feedbackSelection.clear}
                      />

                      {renderForm()}

                      <AboutCustomer
                        ref={aboutComponent}
                        customerReferenceTypes={
                          lookupData?.customerReferenceTypes
                        }
                        responseRequiredOptions={
                          lookupData?.responseRequiredOptions
                        }
                      />
                      <Button
                        onClick={() => submit()}
                        disabled={isSubmitting || !dirty || !isValid}
                        loading={isSubmitting}
                        type="button"
                        className="is-primary"
                      >
                        Submit
                      </Button>
                      <Button
                        onClick={() => {
                          startAgain()
                          setTouched({}, false)
                        }}
                        type="button"
                        className="is-secondary"
                      >
                        Start again
                      </Button>
                    </Form>
                  )}
                </Formik>
              </Conditional>

              <CreateFeedbackFooter />
            </>
          ) : (
            <FeedbackCreatedConfirmation responseRequired={responseRequired} />
          )}
        </article>
      </MainArticleLayout>
    </>
  )
})

export default CreateFeedbackMain
